import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Check, FileDown, X, ZoomIn, Wind, Lock, Sun, Monitor, Layers, Ruler, Square, Fan, Gauge } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import SEO from "@/components/SEO";
import Breadcrumbs, { breadcrumbSchema } from "@/components/Breadcrumbs";
import FAQ, { faqSchema } from "@/components/FAQ";
import { KineticLines, Reveal, SectionHead } from "@/components/Motion";
import { ProductCard } from "@/components/Cards";
import EmbeddedRFQ from "@/components/EmbeddedRFQ";
import NotFound from "@/pages/NotFound";
import { SITE } from "@/config/site";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const featureIcon = (f) => {
  const s = f.toLowerCase();
  if (s.includes("hepa") || s.includes("filtration")) return Wind;
  if (s.includes("interlock")) return Lock;
  if (s.includes("uv")) return Sun;
  if (s.includes("hmi") || s.includes("monitor") || s.includes("control")) return Monitor;
  if (s.includes("304") || s.includes("steel") || s.includes("construction")) return Layers;
  if (s.includes("size") || s.includes("dimension") || s.includes("custom")) return Ruler;
  if (s.includes("glass") || s.includes("view")) return Square;
  if (s.includes("blower") || s.includes("airflow")) return Fan;
  if (s.includes("pressure")) return Gauge;
  return Check;
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // {image, label}
  const [knowledge, setKnowledge] = useState([]);

  useEffect(() => {
    setLoading(true);
    setProduct(null);
    setKnowledge([]);
    api.get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        trackEvent("product_view", { product: slug });
        if (r.data.related_knowledge?.length) {
          api.get("/knowledge").then((k) => {
            setKnowledge(k.data.filter((a) => r.data.related_knowledge.includes(a.slug)));
          }).catch(() => {});
        }
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!loading && !product) return <NotFound />;
  if (!product) return <div className="min-h-[60vh]" />;

  const crumbs = [
    { label: "Products", to: "/products" },
    ...(product.category_info ? [{ label: product.category_info.name, to: `/products/category/${product.category}` }] : []),
    { label: product.name },
  ];
  const imgAlt = product.image_alt || `STJ Innovations ${product.name} — SS 304 cleanroom equipment for pharmaceutical and controlled-environment applications`;

  const jsonLd = [
    breadcrumbSchema(crumbs),
    {
      "@context": "https://schema.org", "@type": "Product",
      name: product.name, description: product.short_description, image: product.image,
      brand: { "@type": "Brand", name: SITE.brand },
      manufacturer: { "@type": "Organization", name: SITE.legalName, address: { "@type": "PostalAddress", addressCountry: "IN" } },
      category: product.category_info?.name,
    },
    ...(product.faqs?.length ? [faqSchema(product.faqs)] : []),
  ];

  const gallery = product.gallery?.length
    ? product.gallery.map((g) => (typeof g === "string" ? { image: g, label: product.name } : g))
    : [{ image: product.image, label: product.name }];

  return (
    <div data-testid="product-detail-page">
      <SEO title={product.seo?.title?.replace(" | STJ Innovations", "") || product.name}
        description={product.seo?.description || product.short_description}
        path={`/products/${slug}`} image={product.image} type="product" jsonLd={jsonLd} />

      {/* A. HERO */}
      <section className="blueprint-dark bg-ink" data-testid="product-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-12 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-16">
          <div className="lg:col-span-7">
            <Breadcrumbs dark items={crumbs} />
            {product.category_info && <p className="eyebrow eyebrow-light">{product.category_info.name}</p>}
            <KineticLines lines={[product.name]}
              className="mt-3 font-display text-3xl font-medium leading-[1.08] tracking-tight text-white sm:text-4xl lg:text-5xl" />
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300">{product.short_description}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#rfq-section" data-testid="product-rfq-button"
                className="group inline-flex items-center justify-center gap-2 bg-accent px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-dark">
                Request a Quote <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
              </a>
              <a href="/STJ-Innovations-Pharmaceutical-Cleanroom-Equipment-Catalogue.pdf" target="_blank" rel="noopener noreferrer"
                data-testid="product-catalogue-button"
                onClick={() => trackEvent("datasheet_download", { document: "product_catalogue", from: `product-hero:${slug}` })}
                className="inline-flex items-center justify-center gap-2 border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-white/50">
                <FileDown size={15} /> Download Catalogue
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.12}>
              <button onClick={() => setLightbox({ image: product.image, label: product.name })}
                className="spotlight-img group relative block w-full overflow-hidden border border-white/10 bg-white/5" aria-label={`Zoom ${product.name} image`}>
                <img src={product.image} alt={imgAlt} className="aspect-[4/3] w-full object-contain p-4" />
                <span className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-ink/80 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-300 opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
                  <ZoomIn size={12} /> Zoom
                </span>
              </button>
              <p className="mt-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                {product.image?.startsWith("/catalogue/") ? "STJ catalogue product visual" : "Representative image — product photographs are added as units are documented"}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* B. OVERVIEW */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20" data-testid="product-overview">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="eyebrow">Overview</p>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
              What the {product.name} Does
            </h2>
            <p className="mt-5 text-base leading-[1.8] text-steel">{product.long_description}</p>
          </div>
          <div className="lg:col-span-5">
            {product.materials?.length > 0 && (
              <div className="border border-line bg-mist p-7">
                <h3 className="font-display text-lg font-semibold text-ink">Construction & Materials</h3>
                <ul className="mt-4 space-y-2.5">
                  {product.materials.map((m, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-steel" data-testid={`material-item-${i}`}>
                      <span className="mt-2 h-px w-5 shrink-0 bg-accent" /> {m}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* B.5 HOW IT WORKS */}
      {product.how_it_works?.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8" data-testid="how-it-works-section">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <p className="eyebrow">Operating Principle</p>
              <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-ink sm:text-3xl">How It Works</h2>
            </div>
            <div className="lg:col-span-8">
              <ol className="space-y-3">
                {product.how_it_works.map((step, i) => (
                  <li key={i} className="flex items-start gap-4 border border-line bg-white p-5" data-testid={`how-step-${i}`}>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center bg-accent font-mono text-xs font-medium text-white">{i + 1}</span>
                    <p className="pt-1 text-sm leading-relaxed text-ink">{step}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>
      )}

      {/* C. KEY FEATURES */}
      {product.features?.length > 0 && (
        <section className="border-y border-line bg-mist" data-testid="features-section">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
            <SectionHead eyebrow="Key Features" title="Engineered Into Every Unit" />
            <div className="mt-10 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
              {product.features.map((f, i) => {
                const Icon = featureIcon(f);
                return (
                  <Reveal key={i} delay={(i % 3) * 0.05} className="bg-white">
                    <div className="flex h-full items-start gap-4 p-6" data-testid={`feature-card-${i}`}>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-line bg-accent-light text-accent">
                        <Icon size={17} />
                      </span>
                      <p className="pt-1.5 text-sm font-medium leading-relaxed text-ink">{f}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* D. GALLERY */}
      {gallery.length > 1 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20" data-testid="gallery-section">
          <SectionHead eyebrow="Product Gallery" title="Product Visuals" />
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {gallery.map((g, i) => (
              <Reveal key={i} delay={(i % 4) * 0.05}>
                <button onClick={() => setLightbox(g)} data-testid={`gallery-item-${i}`}
                  className="group block w-full border border-line bg-white text-left transition-colors hover:border-accent/40">
                  <div className="relative aspect-square overflow-hidden bg-mist">
                    <img src={g.image} alt={g.label} loading="lazy"
                      className="h-full w-full object-contain p-3 transition-transform duration-500 group-hover:scale-[1.03]" />
                    <span className="absolute bottom-2.5 right-2.5 flex items-center gap-1 bg-ink/80 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-slate-300 opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
                      <ZoomIn size={11} /> Zoom
                    </span>
                  </div>
                  <p className="border-t border-line p-3.5 text-xs leading-relaxed text-steel">{g.label}</p>
                </button>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* E. TECHNICAL SPECIFICATIONS */}
      {product.specs?.length > 0 && (
        <section className="border-y border-line bg-mist" data-testid="specs-section">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
            <SectionHead eyebrow="Technical Specifications" title="Specifications" />
            <Reveal delay={0.1}>
              <div className="mt-8 overflow-x-auto border border-line bg-white">
                <table className="spec-table w-full min-w-[560px]">
                  <tbody>
                    {product.specs.map((s, i) => (
                      <tr key={i} data-testid={`spec-row-${i}`}>
                        <th scope="row" className="w-1/3 pl-6 text-left font-mono text-[11px] uppercase tracking-[0.15em] text-steel">{s.key}</th>
                        <td className="pr-6 text-ink">{s.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Reveal>

            {/* F. DIMENSION TABLE */}
            {product.dimension_table && (
              <Reveal delay={0.15}>
                <h3 className="mt-12 font-display text-xl font-semibold text-ink" data-testid="dimension-table-title">
                  {product.dimension_table.title}
                </h3>
                <div className="mt-5 overflow-x-auto border border-line bg-white">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b border-line bg-ink text-left">
                        {product.dimension_table.columns.map((c, i) => (
                          <th key={i} className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-300">{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {product.dimension_table.rows.map((row, i) => (
                        <tr key={i} className="border-b border-line/60 last:border-0" data-testid={`dimension-row-${i}`}>
                          {row.map((cell, j) => (
                            <td key={j} className={`px-5 py-3.5 ${j === 0 ? "font-medium text-ink" : "text-steel"}`}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-xs text-slate-400">{product.dimension_table.note}</p>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* G. TECHNICAL DRAWINGS */}
      {product.drawings?.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20" data-testid="drawings-section">
          <SectionHead eyebrow="Technical Drawings & Dimensions" title="Reference Drawings" />
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {product.drawings.map((d, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <button onClick={() => setLightbox({ image: d.image, label: `${product.name} — ${d.label}` })}
                  data-testid={`drawing-${i}`}
                  className="group block w-full border border-line bg-white transition-colors hover:border-accent/40">
                  <div className="relative aspect-[4/3] overflow-hidden bg-white">
                    <img src={d.image} alt={`${product.name} ${d.label} technical drawing`} loading="lazy"
                      className="h-full w-full object-contain p-5 transition-transform duration-500 group-hover:scale-[1.04]" />
                    <span className="absolute bottom-2.5 right-2.5 flex items-center gap-1 bg-ink/80 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-slate-300 opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
                      <ZoomIn size={11} /> Zoom
                    </span>
                  </div>
                  <p className="border-t border-line p-3.5 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-steel">{d.label}</p>
                </button>
              </Reveal>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-400">All dimensions in mm. Project-specific GA drawings are prepared during quotation.</p>
        </section>
      )}

      {/* H. CONFIGURATIONS & OPTIONS */}
      {product.option_groups?.length > 0 && (
        <section className="border-y border-line bg-mist" data-testid="options-section">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
            <SectionHead eyebrow="Configurations & Options" title="Build It Around Your Process" />
            <div className="mt-12 space-y-12">
              {product.option_groups.map((group, gi) => (
                <div key={gi} data-testid={`option-group-${gi}`}>
                  <h3 className="font-display text-lg font-semibold text-ink">{group.title}</h3>
                  <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {group.items.map((item, ii) => (
                      <Reveal key={ii} delay={ii * 0.05}>
                        <div className="flex h-full items-center gap-4 border border-line bg-white p-4" data-testid={`option-card-${gi}-${ii}`}>
                          {item.image && (
                            <button onClick={() => setLightbox({ image: item.image, label: item.label })}
                              className="h-16 w-16 shrink-0 overflow-hidden border border-line bg-mist" aria-label={`Zoom ${item.label}`}>
                              <img src={item.image} alt={`${item.label} — ${product.name} option`} loading="lazy" className="h-full w-full object-cover" />
                            </button>
                          )}
                          <p className="text-sm font-medium leading-snug text-ink">{item.label}</p>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* I. APPLICATIONS + INDUSTRIES */}
      {(product.applications?.length > 0 || product.industries?.length > 0) && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="grid gap-12 lg:grid-cols-2">
            {product.applications?.length > 0 && (
              <div>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-ink">Typical Applications</h2>
                <ul className="mt-6 space-y-3" data-testid="applications-list">
                  {product.applications.map((a, i) => (
                    <li key={i} className="flex items-center gap-3 border border-line bg-white p-4 text-sm font-medium text-ink" data-testid={`application-item-${i}`}>
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-accent-light font-mono text-[10px] text-accent">{String(i + 1).padStart(2, "0")}</span>
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {product.industries?.length > 0 && (
              <div>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-ink">Industries Served</h2>
                <div className="mt-6 space-y-3" data-testid="product-industries">
                  {product.industries.map((ind) => (
                    <Link key={ind.slug} to={`/industries/${ind.slug}`} data-testid={`product-industry-${ind.slug}`}
                      className="group flex items-center justify-between border border-line bg-white p-4 text-sm font-medium text-ink transition-colors hover:border-accent hover:text-accent">
                      {ind.name}
                      <ArrowRight size={14} className="text-steel transition-all group-hover:translate-x-1 group-hover:text-accent" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* FAQ */}
      {product.faqs?.length > 0 && (
        <section className="mx-auto max-w-4xl px-4 pb-16 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Questions" title="Frequently Asked Questions" />
          <div className="mt-8"><FAQ items={product.faqs} /></div>
        </section>
      )}

      {/* J. RELATED PRODUCTS */}
      {product.related_products?.length > 0 && (
        <section className="border-t border-line bg-mist">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
            <SectionHead eyebrow="Related" title="Related Products" />
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="related-products-grid">
              {product.related_products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* RELATED KNOWLEDGE */}
      {knowledge.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="related-knowledge-section">
          <SectionHead eyebrow="Learn More" title="Related Knowledge Center Articles" />
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {knowledge.map((a) => (
              <Link key={a.slug} to={`/knowledge-center/${a.slug}`} data-testid={`related-knowledge-${a.slug}`}
                className="group border border-line bg-white p-6 transition-colors hover:border-accent/40">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent">{a.topic}</p>
                <h3 className="mt-2 font-display text-base font-semibold leading-snug text-ink group-hover:text-accent">{a.title}</h3>
                <span className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-accent">
                  Read <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* K. EMBEDDED RFQ */}
      <section id="rfq-section" className="blueprint-dark bg-ink" data-testid="rfq-section">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <p className="eyebrow eyebrow-light">RFQ Desk</p>
          <h2 className="mt-4 font-display text-2xl font-medium tracking-tight text-white sm:text-3xl lg:text-4xl">
            Request a Quote for the {product.name}
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-400">
            Share your dimensions, interlock and control preferences, and application. Our engineering team will respond with a configured quotation.
          </p>
          <div className="mt-10 border border-white/10 bg-white/[0.03] p-6 sm:p-9">
            <EmbeddedRFQ productName={product.name} />
          </div>
        </div>
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/95 p-4 backdrop-blur-sm"
            data-lenis-prevent
            onClick={() => setLightbox(null)}
            data-testid="image-lightbox" role="dialog" aria-modal="true" aria-label={lightbox.label}
          >
            <button onClick={() => setLightbox(null)} aria-label="Close image viewer"
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center border border-white/20 text-white transition-colors hover:border-white/60">
              <X size={18} />
            </button>
            <motion.figure
              initial={{ scale: 0.92 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="max-h-[88vh] max-w-4xl" onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.image} alt={lightbox.label} className="max-h-[78vh] w-auto max-w-full object-contain" />
              <figcaption className="mt-4 text-center text-sm text-slate-400">{lightbox.label}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky mobile CTA */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 p-3 backdrop-blur lg:hidden" data-testid="mobile-sticky-cta">
        <a href="#rfq-section" data-testid="sticky-rfq-button"
          className="flex w-full items-center justify-center gap-2 bg-accent py-3 text-sm font-semibold text-white">
          Request a Quote <ArrowRight size={15} />
        </a>
      </div>
    </div>
  );
}
